import React, { useEffect, useState } from 'react'
import { Product } from '../../../../modules/productsListing/types'
import { FormikProps, useFormikContext } from 'formik'
import {
  FieldMetadata,
  FieldMetadataField,
  OfferRecipients,
} from '../../../../modules/privateOffer/types'
import {
  BoxV4,
  DatePickerV2,
  NoteV2,
  PlanType,
  TextAreaV4,
  TextInputV4,
  labraUIDateFormat,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { useStyles } from './PrivateOfferDetailsSection.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { OffersWithErrors } from '../../../../modules/privateOffer/privateOfferCreation/types'
import { useSelector } from '../../../../store'
import { OfferRecipientV3 } from '../../../../common/components/OfferRecipientV3/OfferRecipientV3'
import { OfferRecipientWithTable } from '../../../../common/components/OfferRecipientWithTable/OfferRecipientWithTable'
import { getBuyerNotificationsAllowed } from '../../../../api/marketplace'
import { useAccountId } from '@labrav/common'

export type PrivateOfferDetailsSectionProps = {
  product: Product
  noteDescription?: string
}
export const privateOfferExpirationDateField = {
  title: 'Offer expiration date',
  humanReadableKey: 'Offer expiration date',
  placeHolder: 'Offer expiration date',
  id: 'offerExpirationDate',
  isRequired: false,
  isCustom: false,
  dataType: 'date',
  minDate: 'today',
}
export const privateOfferSubscriptionEndDateField = {
  title: 'Subscription end date',
  humanReadableKey: 'Subscription end date',
  placeHolder: 'Subscription end date',
  id: 'subscriptionEndDate',
  isRequired: false,
  isReadOnly: false,
  isCustom: false,
  dataType: 'date',
  minDate: 'today',
}
export const PrivateOfferDetailsSection: React.FC<
  PrivateOfferDetailsSectionProps
> = ({ product, noteDescription }) => {
  const planType = product.productType
  const isAMI = planType === PlanType.AMI
  const productId = product.productId
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const formikProps = useFormikContext() as FormikProps<OffersWithErrors>
  const { values, errors, touched, handleBlur, handleChange } = formikProps
  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        product.productId
      ]?.fieldMetadata
  )
  /** null: not loaded yet or unknown (avoid treating as false before fetch — prevents banner flash) */
  const [buyerNotificationsAllowed, setBuyerNotificationsAllowed] = useState<
    boolean | null
  >(null)
  useEffect(() => {
    if (!accountId || !productId) {
      setBuyerNotificationsAllowed(null)
      return
    }
    let cancelled = false
    setBuyerNotificationsAllowed(null)
    ;(async () => {
      try {
        const response = await getBuyerNotificationsAllowed(
          accountId,
          productId
        )
        if (cancelled) return
        const allowed = response?.data?.buyerNotificationsAllowed
        setBuyerNotificationsAllowed(
          typeof allowed === 'boolean' ? allowed : null
        )
      } catch {
        if (!cancelled) setBuyerNotificationsAllowed(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [accountId, productId])

  const isOfferRecipientsFromTemplate =
    (
      (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
        ?.offerRecipients as OfferRecipients[]
    )?.length > 0 || false

  const isCompanyNameReadOnly = (
    fieldMetadata?.companyName as FieldMetadataField
  )?.readOnly
  const isCompanyHidden = (fieldMetadata?.companyName as FieldMetadataField)
    ?.hidden
  const isAwsAccountIdReadOnly = (
    fieldMetadata?.awsAccountId as FieldMetadataField
  )?.readOnly
  const isAwsAccountIdHidden = (
    fieldMetadata?.awsAccountId as FieldMetadataField
  )?.hidden
  const isOfferRecipientsReadOnly = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.readOnly
  const isOfferRecipientsHidden = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.hidden
  const isCustomEmailMessageReadOnly = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.readOnly
  const isCustomEmailMessageHidden =
    (fieldMetadata?.emailCustomMessage as FieldMetadataField)?.hidden ?? false

  const isPrivateOfferNameReadOnly = (
    fieldMetadata?.privateOfferName as FieldMetadataField
  )?.readOnly
  const isPrivateOfferNameHidden = (
    fieldMetadata?.privateOfferName as FieldMetadataField
  )?.hidden

  const isOfferExpirationDateReadOnly = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.readOnly
  const isOfferExpirationDateHidden = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.hidden

  const isSubscriptionEndDateReadOnly = (
    fieldMetadata?.subscriptionEndDate as FieldMetadataField
  )?.readOnly
  const isSubscriptionEndDateHidden = (
    fieldMetadata?.subscriptionEndDate as FieldMetadataField
  )?.hidden

  const { classes } = useStyles()
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Offer details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {noteDescription && <NoteV2 description={noteDescription} />}
        {/* company details */}
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {!isCompanyHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="companyName"
                  title={isAMI ? 'Buyer name' : 'Company name'}
                  type="text"
                  placeHolder=""
                  value={values.companyName as string}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'companyName', false) &&
                      get(errors, 'companyName', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={isCompanyNameReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'companyName', false) &&
                      get(errors, 'companyName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
            {!isAwsAccountIdHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="awsAccountId"
                  title="AWS account ID"
                  type="text"
                  placeHolder=""
                  value={(values.awsAccountId || '') as string}
                  maxNumber={12}
                  isError={
                    !!(
                      get(touched, 'awsAccountId', false) &&
                      get(errors, 'awsAccountId', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={isAwsAccountIdReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'awsAccountId', false) &&
                      get(errors, 'awsAccountId', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
          </div>
        </div>
        {/* offer details */}
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {!isPrivateOfferNameHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="privateOfferName"
                  title="Offer name"
                  type="text"
                  placeHolder=""
                  value={values.privateOfferName as string}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'privateOfferName', false) &&
                      get(errors, 'privateOfferName', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={isPrivateOfferNameReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'privateOfferName', false) &&
                      get(errors, 'privateOfferName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
            {!isOfferExpirationDateHidden && (
              <div className={classes.inputWidth}>
                <DatePickerV2
                  format={labraUIDateFormat}
                  size="small"
                  field={{
                    ...privateOfferExpirationDateField,
                    isReadOnly: isOfferExpirationDateReadOnly || false,
                  }}
                  views={['year', 'month', 'day']}
                  key={'offerExpirationDate'}
                  showErrorHelper={
                    !!touched.offerExpirationDate &&
                    !isEmpty(errors.offerExpirationDate)
                  }
                  fieldId={'offerExpirationDate'}
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  errorHelperText={
                    (!!touched.offerExpirationDate &&
                      errors.offerExpirationDate) ||
                    ''
                  }
                  validate
                />
              </div>
            )}
            {product.productType === PlanType.SAAS_SUBSCRIPTION &&
              !isSubscriptionEndDateHidden && (
                <div className={classes.inputWidth}>
                  <DatePickerV2
                    format={labraUIDateFormat}
                    size="small"
                    views={['year', 'month', 'day']}
                    field={{
                      ...privateOfferSubscriptionEndDateField,
                      isReadOnly: isSubscriptionEndDateReadOnly || false,
                    }}
                    key={'subscriptionEndDate'}
                    showErrorHelper={
                      !!touched.subscriptionEndDate &&
                      !isEmpty(errors.subscriptionEndDate)
                    }
                    fieldId={'subscriptionEndDate'}
                    formik={
                      formikProps as unknown as FormikProps<
                        Record<string, unknown>
                      >
                    }
                    errorHelperText={
                      (!!touched.subscriptionEndDate &&
                        errors.subscriptionEndDate) ||
                      ''
                    }
                    validate
                  />
                </div>
              )}
          </div>
          {isAMI && (
            <TextAreaV4
              keyName="offerDescription"
              title="Description (Optional)"
              rows={10}
              maxNumber={1000}
              height={'80px'}
              type="string"
              placeHolder="Add offer description"
              value={(values.offerDescription || '') as string}
              isError={
                !!(
                  get(touched, 'offerDescription', false) &&
                  get(errors, 'offerDescription', false)
                )
              }
              onChange={handleChange}
              isReadOnly={false}
              isRequired={false}
              errorText={
                (!!get(touched, 'offerDescription', false) &&
                  get(errors, 'offerDescription', ' ')) ||
                ' '
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          )}
        </div>

        {isOfferRecipientsFromTemplate ? (
          <>
            <OfferRecipientWithTable
              offerRecipients={
                (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
                  ?.offerRecipients as OfferRecipients[]
              }
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          </>
        ) : (
          <>
            {!isOfferRecipientsHidden && (
              <div
                className={classes.sectionContainer}
                data-testid="recipients-details"
              >
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Offer recipients
                  </Typography>

                  {buyerNotificationsAllowed === false && (
                    <NoteV2
                      description={
                        <Typography className={classes.noteText}>
                          {noteDescription && <span>{noteDescription}</span>}
                          Buyer notifications are turned off in Listings. Offer
                          notifications cannot be sent to buyers via Labra.
                          Enable notifications in Listings to send emails.
                        </Typography>
                      }
                    />
                  )}
                </div>
                <OfferRecipientV3
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  isReadOnly={isOfferRecipientsReadOnly}
                />
              </div>
            )}
          </>
        )}

        {/* custom message */}
        {!isCustomEmailMessageHidden && (
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <div className={classes.sectionHeadingContainer}>
                {' '}
                <Typography className={classes.sectionHeading}>
                  Custom email message
                </Typography>
                <Typography className={classes.sectionText}>
                  Optional
                </Typography>
              </div>
            </div>
            <TextAreaV4
              keyName="emailCustomMessage"
              title="Add a custom email message"
              rows={10}
              maxNumber={1000}
              height={'80px'}
              type="string"
              placeHolder="Add a custom email message"
              value={(values.emailCustomMessage || '') as string}
              isError={
                !!(errors.emailCustomMessage && touched.emailCustomMessage)
              }
              onChange={handleChange}
              isReadOnly={isCustomEmailMessageReadOnly || false}
              isRequired={false}
              errorText={
                (!!touched.emailCustomMessage && errors.emailCustomMessage) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        )}
      </BoxV4>
    </div>
  )
}
